import React from "react"
import { useSelector } from "react-redux"
import DashboardCard from "../../DashboardComponents/DashboardCard"
import CustomButton from "../../OnboardingComponents/CustomButton"
import { useLocation, useNavigate } from "react-router-dom"
import {
  firstDateValue,
  formatDate,
  parseDate,
} from "../../../helpers/formatDate"
import { isAdminPortalUser } from "../../../helpers/adminRoles"

/**
 * ProjectDetailsCard — title + description in the header, with date /
 * created-by rows below (Figma Current / Future / Past).
 */
const ProjectDetailsCard = ({ project, fullHeight = true }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const currentUser = useSelector(state => state.auth?.currentUser)
  const currentUserId = currentUser?.id
  const creatorId =
    project?.user?.id || project?.user_id || project?.created_by || null
  const creatorEmail = project?.user?.email || null
  const startDate = formatDate(
    firstDateValue(project.startDate, project.start_date)
  )
  const rawEnd = firstDateValue(
    project.targetDate,
    project.endDate,
    project.end_date
  )
  const parsedEnd = parseDate(rawEnd)
  const hasOngoingFlag =
    project.isOngoing != null || project.is_ongoing != null
  const isOngoing = hasOngoingFlag
    ? Boolean(project.isOngoing || project.is_ongoing)
    : !parsedEnd
  const isInProgress = project.status?.status === "in-progress"
  const isPast = project.status?.status === "past"
  const endLabel = isInProgress ? "Target Date" : "End Date"
  const endValue = isOngoing ? "Ongoing" : formatDate(rawEnd)
  const daysLeft = Number(project.daysLeft)
  const daysLeftValue = isPast
    ? isOngoing
      ? null
      : "—"
    : project.isOverdue
      ? "Overdue"
      : isOngoing
        ? null
        : Number.isFinite(daysLeft)
          ? `${daysLeft} ${daysLeft === 1 ? "day" : "days"}`
          : null
  const createdByName =
    [project?.user?.first_name, project?.user?.last_name]
      .filter(Boolean)
      .join(" ") ||
    project?.user?.email ||
    "Creator"

  const handleCreatedByClick = () => {
    // Admin / city admin → member profile (same as Members listing)
    if (isAdminPortalUser(currentUser)) {
      const memberKey = creatorEmail || creatorId
      if (!memberKey) return

      const cityMatch = location.pathname.match(/^\/admin\/cities\/([^/]+)/)
      const memberBasePath = cityMatch?.[1]
        ? `/admin/cities/${cityMatch[1]}/members`
        : "/admin/members"

      navigate(`${memberBasePath}/${encodeURIComponent(memberKey)}`)
      return
    }

    if (!creatorId) return

    if (currentUserId && String(creatorId) === String(currentUserId)) {
      navigate(`/profile/${currentUserId}`)
      return
    }

    navigate(`/public/profile/${creatorId}`)
  }

  const canOpenCreator = isAdminPortalUser(currentUser)
    ? Boolean(creatorEmail || creatorId)
    : Boolean(creatorId)

  return (
    <DashboardCard
      title="Project Details"
      subtitle={
        <p className="project-overview__description">{project.description}</p>
      }
      tone="cream"
      fullHeight={fullHeight}
      className="project-overview__details-card"
    >
      <div className="project-overview__details-list">
        <div className="project-overview__detail-row">
          <span className="project-overview__detail-label">Start Date</span>
          <span className="project-overview__detail-value">{startDate}</span>
        </div>
        <div className="project-overview__detail-row">
          <span className="project-overview__detail-label">{endLabel}</span>
          <span className="project-overview__detail-value">{endValue}</span>
        </div>
        {daysLeftValue && (
          <div className="project-overview__detail-row">
            <span className="project-overview__detail-label">Days Left</span>
            <span
              className={`project-overview__detail-value${
                project.isOverdue && !isPast
                  ? " project-overview__detail-value--overdue"
                  : ""
              }`}
            >
              {daysLeftValue}
            </span>
          </div>
        )}
        <div className="project-overview__detail-row">
          <span className="project-overview__detail-label">Created By</span>
          <span className="project-overview__detail-value">
            <CustomButton
              variant="transparent"
              size="sm"
              type="button"
              className="project-overview__created-by"
              onClick={handleCreatedByClick}
              disabled={!canOpenCreator}
            >
              {createdByName}
            </CustomButton>
          </span>
        </div>
      </div>
    </DashboardCard>
  )
}

export default ProjectDetailsCard
